'use client';

import * as React from 'react';

import { Calendar } from './calendar';
import { Icons } from './icons';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '../lib/utils';

export interface FormDatePickerProps {
  id?: string;
  name?: string;
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  fromDate?: Date;
  toDate?: Date;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export function FormDatePicker({
  id,
  name,
  value,
  onChange,
  placeholder = 'MM/DD/YYYY',
  className,
  disabled = false,
  fromDate,
  toDate,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: FormDatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState<Date | undefined>(value);
  const [inputValue, setInputValue] = React.useState(formatInputDate(value));

  React.useEffect(() => {
    setInputValue(formatInputDate(value));
    if (value) {
      setMonth(value);
    }
  }, [value]);

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <input
          id={id}
          name={name}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          className="border-input placeholder:text-muted-foreground focus-visible:ring-ring text-foreground flex h-11 w-full rounded-md border bg-white px-3 py-2 pr-11 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50"
          onChange={(event) => {
            const nextValue = formatPartialDateInput(event.target.value);
            setInputValue(nextValue);

            if (nextValue === '') {
              onChange?.(undefined);
              return;
            }

            const nextDate = parseInputDate(nextValue);
            if (nextDate) {
              setMonth(nextDate);
              onChange?.(nextDate);
            }
          }}
          onBlur={(event) => {
            const nextValue = formatPartialDateInput(event.target.value);
            if (nextValue === '') {
              setInputValue('');
              onChange?.(undefined);
              return;
            }

            const nextDate = parseInputDate(nextValue);
            if (nextDate) {
              const formattedValue = formatInputDate(nextDate);
              setInputValue(formattedValue);
              setMonth(nextDate);
              onChange?.(nextDate);
              return;
            }

            setInputValue(formatInputDate(value));
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
            }
          }}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Select date"
              disabled={disabled}
              className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute right-0 top-0 inline-flex h-11 w-11 items-center justify-center rounded-r-md transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icons.Calendar className="size-4" />
              <span className="sr-only">Select date</span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto overflow-hidden rounded-md border bg-white p-0 shadow-md"
            align="end"
            sideOffset={8}
          >
            <Calendar
              mode="single"
              month={month}
              selected={value}
              onMonthChange={setMonth}
              onSelect={(nextDate) => {
                onChange?.(nextDate);
                setInputValue(formatInputDate(nextDate));
                setMonth(nextDate);
                setOpen(false);
              }}
              fromDate={fromDate}
              toDate={toDate}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

function formatInputDate(date: Date | undefined): string {
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear());

  return `${month}/${day}/${year}`;
}

function parseInputDate(value: string): Date | undefined {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
  if (!match) return undefined;

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    Number.isNaN(date.getTime()) ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getFullYear() !== year
  ) {
    return undefined;
  }

  return date;
}

function formatPartialDateInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}
